<script setup lang="ts">
  import { useCssModule } from 'vue';

  export interface RadioVariantProps {
    /**
     * Whether the input should be disabled
     */
    disabled?: boolean;

    /**
     * Whether the group should expand to the parent's width
     */
    fullWidth?: boolean;

    /**
     * A unique string to distinguish one Radio instance from another
     * and to link the label to the input; required for accessibility
     */
    id: string | number;

    /**
     * The description which appears to the right of the radio button
     */
    label?: string;

    /**
     * value bound to the parent component
     */
    modelValue?: string | undefined;

    /**
     * Passed to the "name" attribute of the `<input>` element.
     */
    name?: string;

    /**
     * The value for the radio input.
     */
    value: string;
  }

  const props = defineProps<RadioVariantProps>();
  const classes = useCssModule();

  const emit = defineEmits<{
    (e: 'input', event: Event): void;
  }>();
</script>

<template>
  <label
    :class="[classes.root, { 'w-full': props.fullWidth, [classes['root--disabled']]: props.disabled }]"
    :for="`${props.name}-${props.id}`"
  >
    <div
      class="flex border"
      :class="[
        classes['tile-header'],
        {
          'border-blue-500 bg-blue-100': props.modelValue === props.value && !props.disabled,
          'border-ice-500 bg-ice-100': props.modelValue !== props.value || props.disabled,
        },
      ]"
    >
      <input
        :id="`${props.name}-${props.id}`"
        :aria-labelledby="`tile-label-${props.name}-${props.id}`"
        class="sr-only"
        :class="classes.input"
        type="radio"
        :name="props.name"
        :value="props.value"
        :checked="props.modelValue === props.value"
        :disabled="props.disabled"
        @input="emit('input', $event)"
      />
      <div :id="`tile-label-${props.name}-${props.id}`" :class="[classes.label, { 'text-ice-500': props.disabled }]">
        {{ props.label }}
      </div>
    </div>
    <div
      :class="[
        classes['tile-body'],
        {
          'border-ice-500': props.modelValue !== props.value || props.disabled,
          'border-blue-500': props.modelValue === props.value && !props.disabled,
        },
      ]"
    >
      <slot></slot>
    </div>
  </label>
</template>

<style module>
  @reference "../../../../styles/main.css";

  @layer utilities {
    .root {
      border-radius: var(--radius-sm);
      cursor: pointer;
      display: flex;
      flex: 1;
      flex-direction: column;
      user-select: none;
    }

    .root--disabled {
      cursor: auto;
    }

    .tile-header,
    .tile-body {
      transition: all 0.2s;
    }

    .tile-header {
      border-radius: var(--radius-sm) var(--radius-sm) 0 0;
      padding: --spacing(3) 0;
    }

    .root:hover:not(.root--disabled) .tile-header {
      background-color: var(--color-blue-100) !important;
      border-color: var(--color-blue-500) !important;
      color: var(--color-ice-900) !important;
      transition: all 0.2s;
    }

    .label {
      font-weight: var(--font-weight-medium);
      user-select: none;
      cursor: pointer;
      padding: 0 --spacing(3);
    }

    .input {
      appearance: none;
      background-color: var(--color-white);
      border-radius: 50%;
      width: 20px;
      height: 20px;
      border: 1px solid var(--color-ice-500);
      transition: all 0.2s;
      position: relative;
      cursor: pointer;
      top: 2px;
      margin-left: --spacing(3);
    }

    .input:checked {
      background-image: radial-gradient(var(--color-blue-500) 50%, transparent 54%);
    }

    .input:disabled:checked {
      background-image: radial-gradient(var(--color-ice-500) 50%, var(--color-ice-100) 54%);
    }

    .tile-body {
      border-radius: 0 0 var(--radius-sm) var(--radius-sm);
      border-width: 0 1px 1px 1px;
      background-color: var(--color-white);
      padding: --spacing(6);
      display: flex;
      flex-direction: column;
    }

    .root:hover:not(.root--disabled) .tile-body {
      border-color: var(--color-blue-500) !important;
      transition: all 0.2s;
    }
  }
</style>
